import React, { useRef } from 'react';
import { Link } from 'react-router-dom';
import { motion, useScroll, useTransform } from 'framer-motion';
import { ArrowRightIcon, ArrowUpRightIcon } from 'lucide-react';
const Home: React.FC = () => {
  const imageRef = useRef<HTMLDivElement>(null);
  const {
    scrollYProgress
  } = useScroll({
    target: imageRef,
    offset: ['start end', 'end start']
  });
  const rotateX = useTransform(scrollYProgress, [0, 1], [15, -15]);
  const rotateY = useTransform(scrollYProgress, [0, 1], [-15, 15]);
  return <div className="min-h-screen" data-id="element-231">
      {/* Hero Section */}
      <section className="min-h-screen pt-32 pb-20 px-4 container mx-auto" data-id="element-232">
        <div className="flex flex-col items-center" data-id="element-233">
          <p className="text-sm font-thin tracking-widest mb-8" data-id="element-234">
            HASSAAN BASIT
          </p>
          <div className="flex flex-col md:flex-row items-center justify-center gap-4 md:gap-8" data-id="element-235">
            <div className="text-right" data-id="element-236">
              <h1 className="text-4xl md:text-6xl font-bold" data-id="element-237">DIGITAL</h1>
              <h1 className="text-4xl md:text-6xl font-bold" data-id="element-238">GROWTH</h1>
            </div>
            <motion.div ref={imageRef} style={{
            rotateX,
            rotateY,
            perspective: 1000
          }} className="relative w-64 h-64 md:w-80 md:h-80 rounded-3xl overflow-hidden my-8 md:my-0" data-id="element-239">
              <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wWvs2P1Egc1Wgg3MxU85S8/Portfolio_Main_Image_.jpg" alt="Hassaan Basit" className="w-full h-full object-cover" data-id="element-240" />
            </motion.div>
            <div className="text-left" data-id="element-241">
              <h1 className="text-4xl md:text-6xl font-bold" data-id="element-242">ARCHITECT</h1>
              <p className="mt-4 font-thin max-w-xs" data-id="element-243">
                I create interconnected Digital Ecosystems for service-based
                businesses, engineered to increase their sales and client
                acquisition.
              </p>
            </div>
          </div>
        </div>
      </section>
      {/* About Me Section */}
      <section className="py-20 px-4 container mx-auto" data-id="element-244">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-245">
          <div data-id="element-246">
            <h2 className="text-3xl md:text-5xl font-bold mb-8" data-id="element-247">ABOUT ME</h2>
            <p className="font-thin mb-8" data-id="element-248">
              Hi, I'm Hassaan—a Digital Growth Architect passionate about
              building systems that create real financial impact. My approach is
              hands-on and results-obsessed, ensuring every part of your digital
              ecosystem works in harmony to achieve one goal: growth.
            </p>
            <div className="grid grid-cols-3 gap-4 mb-8" data-id="element-249">
              <div data-id="element-250">
                <p className="text-2xl font-bold text-[#39FF14]" data-id="element-251">3+</p>
                <p className="font-thin text-sm" data-id="element-252">Years of Experience</p>
              </div>
              <div data-id="element-253">
                <p className="text-2xl font-bold text-[#39FF14]" data-id="element-254">15</p>
                <p className="font-thin text-sm" data-id="element-255">Completed Projects</p>
              </div>
              <div data-id="element-256">
                <p className="text-2xl font-bold text-[#39FF14]" data-id="element-257">25+</p>
                <p className="font-thin text-sm" data-id="element-258">Clients on Worldwide</p>
              </div>
            </div>
            <div className="mb-8" data-id="element-259">
              <p className="font-thin" data-id="element-260">
                Email:{' '}
                <span className="font-normal" data-id="element-261">hassaanbasitali@gmail.com</span>
              </p>
              <p className="font-thin" data-id="element-262">
                Call Today: <span className="font-normal" data-id="element-263">+92 300 1181588</span>
              </p>
            </div>
            <Link to="/about" className="inline-flex items-center border border-[#39FF14] text-[#39FF14] px-6 py-3 rounded-full hover:bg-[#39FF14] hover:text-black transition-all" data-id="element-264">
              MY STORY
              <ArrowRightIcon className="ml-2 w-4 h-4" data-id="element-265" />
            </Link>
          </div>
          <motion.div style={{
          rotateX,
          rotateY,
          perspective: 1000
        }} className="relative w-full h-[400px] rounded-3xl overflow-hidden" data-id="element-266">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/p4xmA955NgrYuGKYwWdBc8/Portfolio_Secondary_Image_.jpg" alt="Hassaan Basit" className="w-full h-full object-cover" data-id="element-267" />
          </motion.div>
        </div>
      </section>
      {/* Featured Projects Section */}
      <section className="py-20 px-4 container mx-auto" data-id="element-268">
        <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-269">
          FEATURED PROJECTS
        </h2>
        <p className="font-thin mb-12 max-w-2xl" data-id="element-270">
          These selected projects reflect my architectural approach—solving real
          business problems through strategic, interconnected digital
          ecosystems. Each one is a story of partnership and measurable growth.
        </p>
        <div className="space-y-8" data-id="element-271">
          <ProjectCard title="From Dormant to Dominant: Reigniting an Accounting Firm's Growth Engine." link="/projects/plus-accountants" image="https://images.unsplash.com/photo-1554224155-6726b3ff858f?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1742&q=80" data-id="element-272" />
          <ProjectCard title="From Invisible to In-Demand: A Digital Turnaround for Dua Construction Services." link="/projects/dua-construction" image="https://images.unsplash.com/photo-1503387762-592deb58ef4e?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1931&q=80" data-id="element-273" />
          <ProjectCard title="The Lean Launch: Generating 35+ Sales on a Shoestring Budget." link="/projects/abh-systems" image="https://images.unsplash.com/photo-1661961110372-8a7682543120?ixlib=rb-4.0.3&ixid=M3wxMjA3fDB8MHxwaG90by1wYWdlfHx8fGVufDB8fHx8fA%3D%3D&auto=format&fit=crop&w=1740&q=80" data-id="element-274" />
        </div>
      </section>
      {/* Testimonials Section */}
      <section className="py-20 px-4 container mx-auto" data-id="element-275">
        <h2 className="text-3xl md:text-5xl font-bold mb-4" data-id="element-276">
          WHAT MY CLIENTS SAY
        </h2>
        <p className="font-thin mb-12 max-w-2xl" data-id="element-277">
          Here's what my clients have shared about their experience working with
          me. My partnerships are built on trust, transparency, and a relentless
          focus on delivering results that make a real financial impact.
        </p>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6" data-id="element-278">
          <div className="bg-white dark:bg-[#121212] p-8 rounded-xl shadow-lg" data-id="element-279">
            <p className="font-thin mb-8" data-id="element-280">
              "Hassaan truly understood my vision and turned it into a powerful
              digital strategy. The results went far beyond my expectations, and
              our revenue has never been better."
            </p>
            <p className="font-normal" data-id="element-281">John Harris, CEO of Plus Accountants</p>
          </div>
          <div className="bg-[#39FF14] p-8 rounded-xl shadow-lg text-black" data-id="element-282">
            <p className="text-5xl font-bold mb-2" data-id="element-283">200%</p>
            <p className="font-thin" data-id="element-284">Client Revenue Growth</p>
          </div>
          <div className="bg-[#F5F5F5] dark:bg-[#1A1A1A] p-8 rounded-xl shadow-lg" data-id="element-285">
            <p className="text-5xl font-bold mb-2" data-id="element-286">98%</p>
            <p className="font-thin" data-id="element-287">Satisfaction Rate</p>
          </div>
        </div>
      </section>
      {/* Contact Section */}
      <section className="py-20 px-4 container mx-auto bg-[#121212] dark:bg-[#0A0A0A] rounded-3xl my-20" data-id="element-288">
        <div className="grid grid-cols-1 md:grid-cols-2 gap-12 items-center" data-id="element-289">
          <div className="rounded-2xl overflow-hidden h-[500px]" data-id="element-290">
            <img src="https://uploadthingy.s3.us-west-1.amazonaws.com/wWvs2P1Egc1Wgg3MxU85S8/Portfolio_Main_Image_.jpg" alt="Hassaan Basit" className="w-full h-full object-cover" data-id="element-291" />
          </div>
          <div data-id="element-292">
            <h2 className="text-3xl md:text-5xl font-bold mb-4 text-white" data-id="element-293">
              YOUR ALL-IN-ONE GROWTH PARTNER
            </h2>
            <p className="font-thin mb-8 text-white" data-id="element-294">
              Why waste time and money juggling multiple freelancers? I provide
              a complete, streamlined digital ecosystem without the high costs
              and headaches of a traditional agency. Book your discovery call to
              get started.
            </p>
            <form className="space-y-6" data-id="element-295">
              <div className="grid grid-cols-2 gap-4" data-id="element-296">
                <div data-id="element-297">
                  <label className="block text-[#39FF14] text-sm font-thin mb-2" data-id="element-298">
                    Name
                  </label>
                  <input type="text" className="w-full bg-[#1A1A1A] rounded-lg p-3 text-white" data-id="element-299" />
                </div>
                <div data-id="element-300">
                  <label className="block text-[#39FF14] text-sm font-thin mb-2" data-id="element-301">
                    Email
                  </label>
                  <input type="email" className="w-full bg-[#1A1A1A] rounded-lg p-3 text-white" data-id="element-302" />
                </div>
              </div>
              <div data-id="element-303">
                <label className="block text-[#39FF14] text-sm font-thin mb-2" data-id="element-304">
                  Service Needed?
                </label>
                <select className="w-full bg-[#1A1A1A] rounded-lg p-3 text-white" data-id="element-305">
                  <option data-id="element-306">The Full Digital Ecosystem Package</option>
                  <option data-id="element-307">Market Research Blueprint</option>
                  <option data-id="element-308">Brand Identity Kit</option>
                  <option data-id="element-309">WordPress Website Development</option>
                  <option data-id="element-310">LinkedIn Authority Builder</option>
                  <option data-id="element-311">Social Media Management & Ads</option>
                  <option data-id="element-312">Other</option>
                </select>
              </div>
              <div data-id="element-313">
                <label className="block text-[#39FF14] text-sm font-thin mb-2" data-id="element-314">
                  What Can I Help You With?
                </label>
                <textarea className="w-full bg-[#1A1A1A] rounded-lg p-3 text-white h-32" placeholder="Hello, I'd like to enquire about..." data-id="element-315"></textarea>
              </div>
              <button type="submit" className="border border-[#39FF14] text-[#39FF14] px-8 py-3 rounded-full hover:bg-[#39FF14] hover:text-black transition-all font-bold" data-id="element-316">
                SEND MESSAGE
              </button>
            </form>
          </div>
        </div>
      </section>
    </div>;
};
interface ProjectCardProps {
  title: string;
  link: string;
  image: string;
}
const ProjectCard: React.FC<ProjectCardProps> = ({
  title,
  link,
  image
}) => {
  return <motion.div whileHover={{
    y: -8
  }} className="relative overflow-hidden rounded-2xl shadow-lg group" data-id="element-317">
      <Link to={link} className="block relative h-[300px]" data-id="element-318">
        <div className="absolute inset-0 bg-gradient-to-b from-transparent to-black opacity-70 z-10" data-id="element-319"></div>
        <img src={image} alt={title} className="w-full h-full object-cover" data-id="element-320" />
        <div className="absolute bottom-0 left-0 p-8 z-20" data-id="element-321">
          <h3 className="text-xl md:text-2xl font-bold text-white" data-id="element-322">{title}</h3>
        </div>
        <motion.div initial={{
        scale: 0,
        opacity: 0
      }} whileHover={{
        scale: 1,
        opacity: 1
      }} className="absolute bottom-8 right-8 bg-[#39FF14] rounded-full p-3 z-20" data-id="element-323">
          <ArrowUpRightIcon className="w-5 h-5 text-black" data-id="element-324" />
        </motion.div>
      </Link>
    </motion.div>;
};
export default Home;